import React, { useEffect, useState } from "react";
import { api, formatErr } from "./lib/api";
import { InfoTip } from "./components/InfoTip";

export default function DigestPanel() {
  const [enabled, setEnabled] = useState(false);
  const [recipientsText, setRecipientsText] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [senderEmail, setSenderEmail] = useState("");
  const [info, setInfo] = useState({ has_api_key: false, api_key_hint: "", sender_email: "", last_sent_date: "", last_sent_mode: "", last_sent_at: "" });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/digest/settings");
      setEnabled(!!data.enabled);
      setRecipientsText((data.recipients || []).join(", "));
      setSenderEmail(data.sender_email || "");
      setInfo({
        has_api_key: !!data.has_api_key,
        api_key_hint: data.api_key_hint || "",
        sender_email: data.sender_email || "",
        last_sent_date: data.last_sent_date || "",
        last_sent_mode: data.last_sent_mode || "",
        last_sent_at: data.last_sent_at || "",
      });
    } catch (e) { console.warn("[DigestPanel] load failed:", e?.response?.status, e?.message); }
  };

  useEffect(() => { load(); }, []);

  const parseRecipients = () =>
    recipientsText
      .split(/[,;\n\s]+/)
      .map((s) => s.trim())
      .filter((s) => s && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(s));

  const save = async () => {
    setBusy(true); setMsg(null);
    try {
      const body = { enabled, recipients: parseRecipients() };
      if (apiKey.trim()) body.resend_api_key = apiKey.trim();
      // Always send sender_email (empty string clears back to default)
      body.sender_email = senderEmail.trim();
      await api.post("/digest/settings", body);
      setApiKey("");
      setMsg({ t: "g", m: "تم الحفظ بنجاح ✓" });
      await load();
    } catch (e) {
      setMsg({ t: "r", m: formatErr(e) });
    } finally { setBusy(false); }
  };

  const sendTest = async () => {
    setBusy(true); setMsg(null);
    try {
      const { data } = await api.post("/digest/test");
      setMsg({ t: "g", m: `تم الإرسال (${data.mode === "digest" ? "ملخص ذكاء" : "تذكير"}) إلى ${data.recipients?.length || 0} مستلم ✓` });
      await load();
    } catch (e) {
      setMsg({ t: "r", m: formatErr(e) });
    } finally { setBusy(false); }
  };

  const recipientsValid = parseRecipients();
  const recipientsCount = recipientsValid.length;
  const recipientsBad = recipientsText.trim() && recipientsCount === 0;

  return (
    <div className="set-sec" data-testid="digest-panel">
      <h4>📧 الإيميل اليومي (1:00 صباحاً السعودية)</h4>
      <p style={{ fontSize: 12, color: "var(--gy)", marginBottom: 14, lineHeight: 1.85 }}>
        يُرسل النظام تلقائياً في الساعة 1:00 صباحاً (توقيت السعودية) ملخّصاً ذكياً للأمس مدعوماً بـ Claude.
        <br />إذا لم يتم رفع بيانات لذلك اليوم، يتم إرسال <b>تذكير برفع البيانات</b> بدلاً من الملخّص.
      </p>

      <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", marginBottom: 14 }}>
        <input
          type="checkbox"
          data-testid="digest-enabled-toggle"
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
          style={{ width: 18, height: 18 }}
        />
        <span style={{ fontSize: 13, fontWeight: 700 }}>تفعيل الإرسال اليومي</span>
      </label>

      <div style={{ display: "grid", gap: 12, marginBottom: 12 }}>
        <div>
          <label style={{ display: "block", fontSize: 12, fontWeight: 700, marginBottom: 6 }}>
            بريد المستلمين <span style={{ color: "var(--gy)", fontWeight: 400 }}>(افصلهم بفاصلة أو سطر جديد)</span>
          </label>
          <textarea
            data-testid="digest-recipients-input"
            value={recipientsText}
            onChange={(e) => setRecipientsText(e.target.value)}
            placeholder="manager@thawany.sa, owner@thawany.sa"
            rows={2}
            style={{
              width: "100%", padding: "10px 12px", border: "1.5px solid var(--bg)",
              borderRadius: 10, fontSize: 13, fontFamily: "inherit", resize: "vertical",
              direction: "ltr", textAlign: "left",
            }}
          />
          <div style={{ fontSize: 11, marginTop: 4, color: recipientsBad ? "var(--rd)" : "var(--gy)" }}>
            {recipientsBad ? "صيغة البريد غير صحيحة" : `${recipientsCount} مستلم صالح`}
          </div>
        </div>

        <div>
          <label style={{ display: "block", fontSize: 12, fontWeight: 700, marginBottom: 6 }}>
            مفتاح Resend API
            {info.has_api_key && (
              <span style={{ color: "var(--gn)", fontWeight: 400, marginRight: 8 }}>
                ✓ تم حفظ مفتاح ({info.api_key_hint})
              </span>
            )}
          </label>
          <input
            type="password"
            data-testid="digest-apikey-input"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder={info.has_api_key ? "اتركه فارغاً للإبقاء على المفتاح الحالي" : "re_xxxxxxxxxxxx"}
            style={{
              width: "100%", padding: "10px 12px", border: "1.5px solid var(--bg)",
              borderRadius: 10, fontSize: 13, direction: "ltr", textAlign: "left",
            }}
          />
          <div style={{ fontSize: 11, marginTop: 4, color: "var(--gy)" }}>
            احصل على المفتاح من <a href="https://resend.com/api-keys" target="_blank" rel="noreferrer" style={{ color: "var(--gd)" }}>resend.com/api-keys</a>
          </div>
        </div>

        <div>
          <label style={{ display: "block", fontSize: 12, fontWeight: 700, marginBottom: 6 }}>
            بريد المُرسِل (Sender)
            {senderEmail === "onboarding@resend.dev" && (
              <span style={{ color: "var(--rd)", fontWeight: 400, marginRight: 8, fontSize: 11 }}>
                ⚠ افتراضي — يرسل فقط لإيميل صاحب حساب Resend
              </span>
            )}
          </label>
          <input
            type="email"
            data-testid="digest-sender-input"
            value={senderEmail}
            onChange={(e) => setSenderEmail(e.target.value)}
            placeholder="noreply@نطاقك"
            style={{
              width: "100%", padding: "10px 12px", border: "1.5px solid var(--bg)",
              borderRadius: 10, fontSize: 13, direction: "ltr", textAlign: "left",
            }}
          />
          <div style={{ fontSize: 11, marginTop: 4, color: "var(--gy)", lineHeight: 1.7 }}>
            💡 <b>مهم:</b> لإرسال لأي بريد مستلم، تحتاج <b>domain متحقق منه</b> في Resend. اذهب إلى{" "}
            <a href="https://resend.com/domains" target="_blank" rel="noreferrer" style={{ color: "var(--gd)" }}>resend.com/domains</a>
            {" "}وأضف <b>نطاقك</b> ثم انسخ DNS records في DNS عندك. بعد التحقق، استخدم بريداً من نطاقك مثل <code>noreply@نطاقك</code>.
            <br />🔸 لو تريد تجربة سريعة بدون domain، اترك الحقل فارغاً واستخدم بريد المستلم نفسه الذي سجّلت به في Resend فقط.
          </div>
        </div>
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button
          data-testid="digest-save-btn"
          className="btn btn-primary"
          onClick={save}
          disabled={busy}
        >
          {busy ? "جارٍ الحفظ..." : "💾 حفظ الإعدادات"}
        </button>
        <button
          data-testid="digest-test-btn"
          className="btn btn-success"
          onClick={sendTest}
          disabled={busy || !enabled || recipientsCount === 0 || (!info.has_api_key && !apiKey.trim())}
 
         aria-label={!enabled ? "فعّل الإرسال أولاً" : recipientsCount === 0 ? "أضف بريد مستلم واحد على الأقل" : (!info.has_api_key && !apiKey.trim()) ? "أضف مفتاح Resend أولاً" : ""}>
          {busy ? "جارٍ الإرسال..." : "📨 إرسال اختبار الآن"}
        </button>
      </div>

      {msg && (
        <div data-testid="digest-msg" style={{ marginTop: 12, fontSize: 12, color: msg.t === "g" ? "var(--gn)" : "var(--rd)" }}>
          {msg.m}
        </div>
      )}

      {info.last_sent_at && (
        <div style={{ marginTop: 14, padding: 10, background: "var(--bg)", borderRadius: 8, fontSize: 11.5, color: "var(--gy)" }}>
          آخر إرسال: <b>{info.last_sent_date}</b> — النوع: <b>{info.last_sent_mode === "digest" ? "ملخّص ذكاء" : "تذكير"}</b>
        </div>
      )}
    </div>
  );
}
